import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * Custom hook to fetch crawler failures from Firestore and provide status counts
 * @returns {Object} Object containing counts for different statuses, loading state, and error
 */
function useCrawlerFailures() {
  const [counts, setCounts] = useState({
    pending: 0,
    investigated: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCrawlerFailures = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Query for pending and investigated failures
        const failuresRef = collection(db, 'crawlerFailures');
        const pendingQuery = query(
          failuresRef,
          where('status', '==', 'pending'),
        );
        const investigatedQuery = query(
          failuresRef,
          where('status', '==', 'investigated'),
        );

        // Execute queries in parallel
        const [pendingSnapshot, investigatedSnapshot] = await Promise.all([
          getDocs(pendingQuery),
          getDocs(investigatedQuery),
        ]);

        const pendingCount = pendingSnapshot.size;
        const investigatedCount = investigatedSnapshot.size;
        const totalCount = pendingCount + investigatedCount;

        // Only update state if component is still mounted
        if (isMounted) {
          setCounts({
            pending: pendingCount,
            investigated: investigatedCount,
            total: totalCount,
          });
        }
      } catch (err) {
        console.error('Error fetching crawler failures:', err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCrawlerFailures();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  return { counts, loading, error };
}

export default useCrawlerFailures;
